
import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import MiniLedger from './MiniLedger';
import ListesPermanentes from './ListesPermanentes';
import LoginForm from './LoginForm';
import ActionBar from "./ActionBar";
import { ensureWebApiOnWindow } from './api/webApi';
import AnnualWorkHoursCalculator from "./AnnualWorkHoursCalculator";
import SportTracker from './SportTracker';
import MainNavigation from './MainNavigation';
import SelectionToolbar from './SelectionToolbar';
import UndoToolbar from './UndoToolbar';
import CalendarNavigation from './CalendarNavigation';
import TaskModal from './TaskModal';
import BulkActionsModal from './BulkActionsModal';
import CalendarDay from './CalendarDay';
import ThemeMenu from './ThemeMenu';

ensureWebApiOnWindow();

const TASK_ACTIONS_COMPACT_BREAKPOINT = 1500;

function App() {
  const [tasks, setTasks] = useState([]);
  const [sportEntries, setSportEntries] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskDate, setTaskDate] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkDate, setBulkDate] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [activeView, setActiveView] = useState('tasks');
  const [taskDisplayMode, setTaskDisplayMode] = useState(() => {
    try {
      return localStorage.getItem('lfat-task-display-mode') || 'order';
    } catch {
      return 'order';
    }
  });
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [openMenuTaskId, setOpenMenuTaskId] = useState(null);
  const [showOtherDatePicker, setShowOtherDatePicker] = useState(false);
  const [undoAction, setUndoAction] = useState(null);

  const [themes, setThemes] = useState([]);
  const [themeMenuTask, setThemeMenuTask] = useState(null);
  const [themeError, setThemeError] = useState('');

  const titleInputRef = useRef(null);
  const descriptionTextareaRef = useRef(null);

  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowIso = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(
    tomorrow.getDate()
  ).padStart(2, '0')}`;

  const [goalMinutes, setGoalMinutes] = useState(900);
  
  const handleCloseThemeMenu = () => {
    setThemeMenuTask(null);
    setThemeError('');
  };


const handleOpenThemeMenu = async (task) => {
  setOpenMenuTaskId(null);
  setThemeError('');

  let availableThemes = themes;

  if (availableThemes.length === 0) {
    availableThemes = await loadThemes();
  }

  if (availableThemes.length === 0) {
    setThemeError('Aucun thème disponible.');
    return;
  }

  setThemeMenuTask(task);
};

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const CHECKBOX_LINE_REGEX = /^(\s*)-\s*\[( |x|X)\]\s?(.*)$/;

  const parseDescriptionLines = (value = '') =>
    String(value)
      .split(/\r?\n/)
      .map((line) => {
        const match = line.match(CHECKBOX_LINE_REGEX);
        if (!match) {
          return {
            type: 'text',
            raw: line,
            text: line,
          };
        }

        return {
          type: 'checkbox',
          raw: line,
          checked: /x/i.test(match[2] || ''),
          text: match[3] || '',
          indent: match[1] || '',
        };
      });

  const renderDescriptionHtml = (value = '') =>
    parseDescriptionLines(value)
      .map((line) => {
        if (line.type === 'checkbox') {
          return `
            <div class="planner-checkbox-line ${line.checked ? 'is-checked' : ''}">
              <span class="planner-checkbox-box">${line.checked ? '&#10003;' : ''}</span>
              <span>${escapeHtml(line.text || '')}</span>
            </div>
          `;
        }

        if (!line.text) {
          return '<div class="planner-text-line planner-text-line-empty">&nbsp;</div>';
        }

        return `<div class="planner-text-line">${escapeHtml(line.text)}</div>`;
      })
      .join('');

  const formatIsoDayLabel = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(`${isoDate}T12:00:00`);
    if (Number.isNaN(date.getTime())) return isoDate;

    const label = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    return label.replace(/^./, (char) => char.toUpperCase());
  };

  const formatMonthTitle = (year, monthIndex) => {
    const date = new Date(year, monthIndex, 1);
    return date
      .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      .replace(/^./, (char) => char.toUpperCase());
  };

  const compareTasksForPrint = (a, b) => {
    const dateCompare = normalizeDate(a.task_date).localeCompare(normalizeDate(b.task_date));
    if (dateCompare !== 0) return dateCompare;

    const orderCompare = Number(a.ordre_affichage || 9999) - Number(b.ordre_affichage || 9999);
    if (orderCompare !== 0) return orderCompare;

    return (a.title || '').localeCompare(b.title || '', 'fr', { sensitivity: 'base' });
  };


  const buildPrintDocumentHtml = ({
  scopeTitle,
  scopeSubtitle,
  tasks: sourceTasks,
  forceSingleMonthPage = false,
  layoutMode = 'agenda',
  blankMonth = false,
  targetYear = null,
  targetMonthIndex = null,
}) => {
  const sortedTasks = [...sourceTasks].sort(compareTasksForPrint);

  if (layoutMode === 'month-grid') {
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    const groupedByMonth = new Map();

    if (blankMonth && targetYear !== null && targetMonthIndex !== null) {
      const monthKey = `${targetYear}-${String(targetMonthIndex + 1).padStart(2, '0')}`;
      groupedByMonth.set(monthKey, []);
    } else {
      sortedTasks.forEach((task) => {
        const normalizedDate = normalizeDate(task.task_date);
        if (!normalizedDate) return;

        const [year, month] = normalizedDate.split('-');
        const monthKey = `${year}-${month}`;

        if (!groupedByMonth.has(monthKey)) {
          groupedByMonth.set(monthKey, []);
        }

        groupedByMonth.get(monthKey).push({
          ...task,
          task_date: normalizedDate,
        });
      });
    }

    const buildMonthGrid = (year, monthIndex, monthTasks) => {
      const firstDay = new Date(year, monthIndex, 1);
      const lastDay = new Date(year, monthIndex + 1, 0);
      const daysInMonth = lastDay.getDate();

      const firstDayIndex = (firstDay.getDay() + 6) % 7;

      const tasksByDate = new Map();

      monthTasks.forEach((task) => {
        const key = normalizeDate(task.task_date);
        if (!tasksByDate.has(key)) {
          tasksByDate.set(key, []);
        }
        tasksByDate.get(key).push(task);
      });

      const cells = [];

      for (let i = 0; i < firstDayIndex; i += 1) {
        cells.push(`
          <div class="planner-grid-cell planner-grid-cell-empty">
            <div class="planner-day-number planner-day-number-empty">&nbsp;</div>
            <div class="planner-day-content"></div>
          </div>
        `);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const isoDate = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayTasks = blankMonth ? [] : (tasksByDate.get(isoDate) || []);

        const tasksHtml = dayTasks.length
          ? `
            <ul class="planner-task-simple-list">
              ${dayTasks
                .map((task) => {
                  const priority = Math.max(0, Math.min(4, Number(task.important) || 0));
                  const importantStar = priority > 0
                    ? `<span class="planner-important-star" title="Priorité ${priority} sur 4">${'★'.repeat(priority)}</span>`
                    : '';
                  const doneClass = task.status === 'done' ? 'is-done' : '';
                  return `
                    <li class="planner-task-simple-item ${doneClass}">
                      ${importantStar}<span>${escapeHtml(task.title || 'Sans titre')}</span>
                    </li>
                  `;
                })
                .join('')}
            </ul>
          `
          : `<div class="planner-empty-slot"></div>`;

        cells.push(`
          <div class="planner-grid-cell">
            <div class="planner-day-number">${day}</div>
            <div class="planner-day-content">
              ${tasksHtml}
            </div>
          </div>
        `);
      }

      const totalCells = cells.length <= 35 ? 35 : 42;
      while (cells.length < totalCells) {
        cells.push(`
          <div class="planner-grid-cell planner-grid-cell-empty">
            <div class="planner-day-number planner-day-number-empty">&nbsp;</div>
            <div class="planner-day-content"></div>
          </div>
        `);
      }

      return `
        <section class="${forceSingleMonthPage ? 'planner-month-section planner-month-page' : 'planner-month-section'}">
          <header class="planner-month-header">
            <h2>${escapeHtml(formatMonthTitle(year, monthIndex))}</h2>
          </header>

          <div class="planner-weekdays">
            ${weekDays.map((label) => `<div class="planner-weekday">${label}</div>`).join('')}
          </div>

          <div class="planner-month-grid">
            ${cells.join('')}
          </div>
        </section>
      `;
    };

    const monthsHtml = Array.from(groupedByMonth.entries())
      .map(([monthKey, monthTasks]) => {
        const [year, month] = monthKey.split('-');
        return buildMonthGrid(Number(year), Number(month) - 1, monthTasks);
      })
      .join('');

    const emptyStateHtml =
      monthsHtml.trim() === ''
        ? '<p class="planner-empty-state">Aucune tâche trouvée pour cette période.</p>'
        : monthsHtml;

    return `
      <html>
        <head>
          <title>${escapeHtml(scopeTitle)}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 10mm;
            }

            * {
              box-sizing: border-box;
            }

            html, body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #111827;
              font-family: Arial, Helvetica, sans-serif;
            }

            body {
              font-size: 11px;
              line-height: 1.3;
            }

            .planner-print-root {
              width: 100%;
            }

            .planner-print-header {
              margin-bottom: 10px;
              padding-bottom: 8px;
              border-bottom: 2px solid #111827;
            }

            .planner-print-title {
              margin: 0 0 4px;
              font-size: 24px;
              font-weight: 700;
            }

            .planner-print-subtitle {
              margin: 0;
              font-size: 12px;
              color: #475467;
            }

            .planner-month-section {
              width: 100%;
            }

            .planner-month-page {
              page-break-after: always;
              break-after: page;
            }

            .planner-month-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }

            .planner-month-header {
              margin: 0 0 8px;
              padding-bottom: 4px;
            }

            .planner-month-header h2 {
              margin: 0;
              font-size: 22px;
              font-weight: 700;
              text-transform: capitalize;
            }

            .planner-weekdays {
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              gap: 0;
              margin-bottom: 0;
            }

            .planner-weekday {
              border: 1px solid #000;
              border-bottom: none;
              padding: 8px 6px;
              text-align: center;
              font-size: 12px;
              font-weight: 700;
              background: #f3f4f6;
            }

            .planner-month-grid {
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              gap: 0;
              border-right: 1px solid #000;
              border-bottom: 1px solid #000;
            }

            .planner-grid-cell {
              min-height: 150px;
              border-left: 1px solid #000;
              border-top: 1px solid #000;
              display: flex;
              flex-direction: column;
              background: #fff;
              overflow: hidden;
            }

            .planner-grid-cell-empty {
              background: #fafafa;
            }

            .planner-day-number {
              padding: 6px 8px;
              border-bottom: 1px solid #d1d5db;
              font-size: 22px;
              font-weight: 700;
              line-height: 1;
              text-align: right;
              background: #f9fafb;
            }

            .planner-day-number-empty {
              color: transparent;
            }

            .planner-day-content {
              flex: 1;
              padding: 6px 8px;
            }

            .planner-task-simple-list {
              margin: 0;
              padding: 0;
              list-style: none;
            }

            .planner-task-simple-item {
              margin: 0 0 4px;
              padding: 0;
              font-size: 11px;
              line-height: 1.25;
              display: flex;
              align-items: flex-start;
              gap: 4px;
              word-break: break-word;
            }

            .planner-task-simple-item::before {
              content: "✔";
              font-weight: 700;
              flex: 0 0 auto;
            }

            .planner-task-simple-item.is-done {
              color: #7f1d1d;
            }

            .planner-important-star {
              flex: 0 0 auto;
              margin-right: 1px;
            }

            .planner-empty-slot {
              min-height: 16px;
            }

            .planner-empty-state {
              font-size: 14px;
              color: #475467;
            }
          </style>
        </head>
        <body>
          <div class="planner-print-root">
            <header class="planner-print-header">
              <h1 class="planner-print-title">${escapeHtml(scopeTitle)}</h1>
              <p class="planner-print-subtitle">${escapeHtml(scopeSubtitle || '')}</p>
            </header>
            ${emptyStateHtml}
          </div>
        </body>
      </html>
    `;
  }

  const groups = new Map();

  sortedTasks.forEach((task) => {
    const normalizedDate = normalizeDate(task.task_date);
    if (!normalizedDate) return;

    const [year, monthPart] = normalizedDate.split('-');
    const monthKey = `${year}-${monthPart}`;

    if (!groups.has(monthKey)) {
      groups.set(monthKey, {
        title: formatMonthTitle(Number(year), Number(monthPart) - 1),
        days: new Map(),
      });
    }

    const monthGroup = groups.get(monthKey);

    if (!monthGroup.days.has(normalizedDate)) {
      monthGroup.days.set(normalizedDate, []);
    }

    monthGroup.days.get(normalizedDate).push(task);
  });

  const monthsHtml = Array.from(groups.values())
    .map((monthGroup) => {
      const daysHtml = Array.from(monthGroup.days.entries())
        .map(([dayKey, dayTasks]) => {
          const tasksHtml = dayTasks
            .map((task) => {
              const statusLabel = task.status === 'done' ? 'Fait' : 'À faire';
              const statusClass = task.status === 'done' ? 'done' : 'todo';
              const priority = Math.max(0, Math.min(4, Number(task.important) || 0));
              const importantHtml = priority > 0
                ? `<span class="planner-badge planner-badge-important">Priorité ${'★'.repeat(priority)}</span>`
                : '';
              const descriptionHtml =
                task.description && task.description.trim() !== ''
                  ? `<div class="planner-task-desc">${escapeHtml(task.description).replace(/\r?\n/g, '<br>')}</div>`
                  : '';

              return `
                <article class="planner-task-item ${statusClass}">
                  <div class="planner-task-head">
                    <div class="planner-task-title">${escapeHtml(task.title || 'Sans titre')}</div>
                    <div class="planner-task-badges">
                      <span class="planner-badge planner-badge-status ${statusClass}">${statusLabel}</span>
                      ${importantHtml}
                    </div>
                  </div>
                  ${descriptionHtml}
                </article>
              `;
            })
            .join('');

          return `
            <section class="planner-day-group">
              <div class="planner-day-title">${escapeHtml(formatIsoDayLabel(dayKey))}</div>
              <div class="planner-task-list">${tasksHtml}</div>
            </section>
          `;
        })
        .join('');

      const monthClasses = forceSingleMonthPage ? 'planner-month-section planner-month-page' : 'planner-month-section';

      return `
        <section class="${monthClasses}">
          <header class="planner-month-header">
            <h2>${escapeHtml(monthGroup.title)}</h2>
          </header>
          ${daysHtml}
        </section>
      `;
    })
    .join('');

  const emptyStateHtml =
    monthsHtml.trim() === ''
      ? '<p class="planner-empty-state">Aucune tâche trouvée pour cette période.</p>'
      : monthsHtml;

  return `
    <html>
      <head>
        <title>${escapeHtml(scopeTitle)}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 12mm;
          }

          * {
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #101828;
            font-family: Arial, Helvetica, sans-serif;
          }

          body {
            font-size: 12px;
            line-height: 1.45;
          }

          .planner-print-root {
            padding: 0;
          }

          .planner-print-header {
            margin-bottom: 14px;
            padding-bottom: 8px;
            border-bottom: 2px solid #111827;
          }

          .planner-print-title {
            margin: 0 0 4px;
            font-size: 24px;
            font-weight: 700;
          }

          .planner-print-subtitle {
            margin: 0;
            font-size: 12px;
            color: #475467;
          }

          .planner-month-section {
            margin: 0 0 18px;
          }

          .planner-month-page {
            page-break-after: always;
            break-after: page;
            min-height: calc(100vh - 24mm);
          }

          .planner-month-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          .planner-month-header {
            margin: 0 0 10px;
            padding: 0 0 6px;
            border-bottom: 1px solid #d0d5dd;
          }

          .planner-month-header h2 {
            margin: 0;
            font-size: 18px;
            font-weight: 700;
          }

          .planner-day-group {
            margin: 0 0 12px;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .planner-day-title {
            margin: 0 0 6px;
            font-size: 12px;
            font-weight: 700;
            color: #344054;
            letter-spacing: 0.01em;
          }

          .planner-task-list {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .planner-task-item {
            border: 1px solid #d0d5dd;
            border-left-width: 4px;
            border-radius: 8px;
            padding: 7px 9px;
            background: #ffffff;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .planner-task-item.todo {
            border-left-color: #0f766e;
          }

          .planner-task-item.done {
            border-left-color: #b42318;
          }

          .planner-task-head {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 8px;
          }

          .planner-task-title {
            flex: 1;
            min-width: 0;
            font-size: 13px;
            font-weight: 700;
            line-height: 1.35;
          }

          .planner-task-badges {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .planner-badge {
            display: inline-flex;
            align-items: center;
            padding: 2px 7px;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 700;
            border: 1px solid transparent;
            white-space: nowrap;
          }

          .planner-badge-status.todo {
            background: #ecfdf3;
            color: #027a48;
            border-color: #abefc6;
          }

          .planner-badge-status.done {
            background: #fef3f2;
            color: #b42318;
            border-color: #fecdca;
          }

          .planner-badge-important {
            background: #fff7ed;
            color: #c2410c;
            border-color: #fdba74;
          }

          .planner-task-desc {
            margin-top: 6px;
            color: #344054;
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 12px;
            line-height: 1.5;
          }

          .planner-empty-state {
            margin-top: 24px;
            font-size: 14px;
            color: #667085;
          }
        </style>
      </head>
      <body>
        <main class="planner-print-root">
          <header class="planner-print-header">
            <h1 class="planner-print-title">${escapeHtml(scopeTitle)}</h1>
            <p class="planner-print-subtitle">${escapeHtml(scopeSubtitle)}</p>
          </header>
          ${emptyStateHtml}
        </main>
      </body>
    </html>
  `;
};


// //   scopeTitle,
// //   scopeSubtitle,
// //   tasks: sourceTasks,
// //   forceSingleMonthPage = false
// // }) => {
// //   const sortedTasks = [...sourceTasks].sort(compareTasksForPrint);

// //   const groupedByMonth = new Map();

// //   sortedTasks.forEach((task) => {
// //     const normalizedDate = normalizeDate(task.task_date);
// //     if (!normalizedDate) return;

// //     const [year, month] = normalizedDate.split('-');
// //     const monthKey = `${year}-${month}`;

// //     if (!groupedByMonth.has(monthKey)) {
// //       groupedByMonth.set(monthKey, []);
// //     }

// //     groupedByMonth.get(monthKey).push({
// //       ...task,
// //       task_date: normalizedDate,
// //     });
// //   });

// //   const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// //   const buildMonthGrid = (year, monthIndex, monthTasks) => {
// //     const firstDay = new Date(year, monthIndex, 1);
// //     const lastDay = new Date(year, monthIndex + 1, 0);
// //     const daysInMonth = lastDay.getDate();

// //     // JS: dimanche=0 ... samedi=6
// //     // nous voulons lundi=0 ... dimanche=6
// //     const firstDayIndex = (firstDay.getDay() + 6) % 7;

// //     const tasksByDate = new Map();

// //     monthTasks.forEach((task) => {
// //       const key = normalizeDate(task.task_date);
// //       if (!tasksByDate.has(key)) {
// //         tasksByDate.set(key, []);
// //       }
// //       tasksByDate.get(key).push(task);
// //     });

// //     const cells = [];

// //     // cases vides avant le 1er jour
// //     for (let i = 0; i < firstDayIndex; i += 1) {
// //       cells.push(`
// //         <div class="planner-grid-cell planner-grid-cell-empty">
// //           <div class="planner-day-number planner-day-number-empty">&nbsp;</div>
// //           <div class="planner-day-content"></div>
// //         </div>
// //       `);
// //     }

// //     // jours du mois
// //     for (let day = 1; day <= daysInMonth; day += 1) {
// //       const isoDate = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
// //       const dayTasks = tasksByDate.get(isoDate) || [];

// //       const tasksHtml = dayTasks.length
// //         ? `
// //           <ul class="planner-task-simple-list">
// //             ${dayTasks
// //               .map((task) => {
// //                 const priority = Math.max(0, Math.min(4, Number(task.important) || 0));
// //                 const importantStar = priority > 0
// //                   ? `<span class="planner-important-star" title="Priorité ${priority} sur 4">${'★'.repeat(priority)}</span>`
// //                   : '';
// //                 const doneClass = task.status === 'done' ? 'is-done' : '';
// //                 return `
// //                   <li class="planner-task-simple-item ${doneClass}">
// //                     ${importantStar}<span>${escapeHtml(task.title || 'Sans titre')}</span>
// //                   </li>
// //                 `;
// //               })
// //               .join('')}
// //           </ul>
// //         `
// //         : `<div class="planner-empty-slot"></div>`;

// //       cells.push(`
// //         <div class="planner-grid-cell">
// //           <div class="planner-day-number">${day}</div>
// //           <div class="planner-day-content">
// //             ${tasksHtml}
// //           </div>
// //         </div>
// //       `);
// //     }

// //     // compléter la grille jusqu'à 35 ou 42 cases
// //     const totalCells = cells.length <= 35 ? 35 : 42;
// //     while (cells.length < totalCells) {
// //       cells.push(`
// //         <div class="planner-grid-cell planner-grid-cell-empty">
// //           <div class="planner-day-number planner-day-number-empty">&nbsp;</div>
// //           <div class="planner-day-content"></div>
// //         </div>
// //       `);
// //     }

// //     return `
// //       <section class="${forceSingleMonthPage ? 'planner-month-section planner-month-page' : 'planner-month-section'}">
// //         <header class="planner-month-header">
// //           <h2>${escapeHtml(formatMonthTitle(year, monthIndex))}</h2>
// //         </header>

// //         <div class="planner-weekdays">
// //           ${weekDays.map((label) => `<div class="planner-weekday">${label}</div>`).join('')}
// //         </div>

// //         <div class="planner-month-grid">
// //           ${cells.join('')}
// //         </div>
// //       </section>
// //     `;
// //   };

// //   const monthsHtml = Array.from(groupedByMonth.entries())
// //     .map(([monthKey, monthTasks]) => {
// //       const [year, month] = monthKey.split('-');
// //       return buildMonthGrid(Number(year), Number(month) - 1, monthTasks);
// //     })
// //     .join('');

// //   const emptyStateHtml =
// //     monthsHtml.trim() === ''
// //       ? '<p class="planner-empty-state">Aucune tâche trouvée pour cette période.</p>'
// //       : monthsHtml;

// //   return `
// //     <html>
// //       <head>
// //         <title>${escapeHtml(scopeTitle)}</title>
// //         <style>
// //           @page {
// //             size: A4 landscape;
// //             margin: 10mm;
// //           }

// //           * {
// //             box-sizing: border-box;
// //           }

// //           html, body {
// //             margin: 0;
// //             padding: 0;
// //             background: #ffffff;
// //             color: #111827;
// //             font-family: Arial, Helvetica, sans-serif;
// //           }

// //           body {
// //             font-size: 11px;
// //             line-height: 1.3;
// //           }

// //           .planner-print-root {
// //             width: 100%;
// //           }

// //           .planner-print-header {
// //             margin-bottom: 10px;
// //             padding-bottom: 8px;
// //             border-bottom: 2px solid #111827;
// //           }

// //           .planner-print-title {
// //             margin: 0 0 4px;
// //             font-size: 24px;
// //             font-weight: 700;
// //           }

// //           .planner-print-subtitle {
// //             margin: 0;
// //             font-size: 12px;
// //             color: #475467;
// //           }

// //           .planner-month-section {
// //             width: 100%;
// //           }

// //           .planner-month-page {
// //             page-break-after: always;
// //             break-after: page;
// //           }

// //           .planner-month-page:last-child {
// //             page-break-after: auto;
// //             break-after: auto;
// //           }

// //           .planner-month-header {
// //             margin: 0 0 8px;
// //             padding-bottom: 4px;
// //           }

// //           .planner-month-header h2 {
// //             margin: 0;
// //             font-size: 22px;
// //             font-weight: 700;
// //             text-transform: capitalize;
// //           }

// //           .planner-weekdays {
// //             display: grid;
// //             grid-template-columns: repeat(7, 1fr);
// //             gap: 0;
// //             margin-bottom: 0;
// //           }

// //           .planner-weekday {
// //             border: 1px solid #000;
// //             border-bottom: none;
// //             padding: 8px 6px;
// //             text-align: center;
// //             font-size: 12px;
// //             font-weight: 700;
// //             background: #f3f4f6;
// //           }

// //           .planner-month-grid {
// //             display: grid;
// //             grid-template-columns: repeat(7, 1fr);
// //             gap: 0;
// //             border-right: 1px solid #000;
// //             border-bottom: 1px solid #000;
// //           }

// //           .planner-grid-cell {
// //             min-height: 150px;
// //             border-left: 1px solid #000;
// //             border-top: 1px solid #000;
// //             display: flex;
// //             flex-direction: column;
// //             background: #fff;
// //             overflow: hidden;
// //           }

// //           .planner-grid-cell-empty {
// //             background: #fafafa;
// //           }

// //           .planner-day-number {
// //             padding: 6px 8px;
// //             border-bottom: 1px solid #d1d5db;
// //             font-size: 22px;
// //             font-weight: 700;
// //             line-height: 1;
// //             text-align: right;
// //             background: #f9fafb;
// //           }

// //           .planner-day-number-empty {
// //             color: transparent;
// //           }

// //           .planner-day-content {
// //             flex: 1;
// //             padding: 6px 8px;
// //           }

// //           .planner-task-simple-list {
// //             margin: 0;
// //             padding: 0;
// //             list-style: none;
// //           }

// //           .planner-task-simple-item {
// //             margin: 0 0 4px;
// //             padding: 0;
// //             font-size: 11px;
// //             line-height: 1.25;
// //             display: flex;
// //             align-items: flex-start;
// //             gap: 4px;
// //             word-break: break-word;
// //           }

// //           .planner-task-simple-item::before {
// //             content: "✔";
// //             font-weight: 700;
// //             flex: 0 0 auto;
// //           }

// //           .planner-task-simple-item.is-done {
// //             color: #7f1d1d;
// //           }

// //           .planner-important-star {
// //             flex: 0 0 auto;
// //             margin-right: 1px;
// //           }

// //           .planner-empty-slot {
// //             min-height: 16px;
// //           }

// //           .planner-empty-state {
// //             font-size: 14px;
// //             color: #475467;
// //           }
// //         </style>
// //       </head>
// //       <body>
// //         <div class="planner-print-root">
// //           <header class="planner-print-header">
// //             <h1 class="planner-print-title">${escapeHtml(scopeTitle)}</h1>
// //             <p class="planner-print-subtitle">${escapeHtml(scopeSubtitle || '')}</p>
// //           </header>

// //           ${emptyStateHtml}
// //         </div>
// //       </body>
// //     </html>
// //   `;
// // };

  const openPrintHtml = ({ html }) => {
    const printWindow = window.open('', '', 'width=980,height=900');

    if (!printWindow) return false;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    const triggerPrint = () => {
      printWindow.print();
    };

    if (typeof printWindow.requestAnimationFrame === 'function') {
      printWindow.requestAnimationFrame(() => {
        setTimeout(triggerPrint, 150);
      });
    } else {
      setTimeout(triggerPrint, 150);
    }

    return true;
  };

  const exportPdfDirectIfAvailable = async ({ fileName, html }) => {
    if (window.api && typeof window.api.exportPlanningPdf === 'function') {
      try {
        await window.api.exportPlanningPdf({ fileName, html });
        return true;
      } catch (error) {
        console.error('exportPlanningPdf:', error);
      }
    }

    return openPrintHtml({ html });
  };

const handlePrintCurrentMonth = async () => {
  const year = currentDate.getFullYear();
  const monthIndex = currentDate.getMonth();
  const periodTasks = tasks.filter((task) => {
    const normalizedDate = normalizeDate(task.task_date);
    if (!normalizedDate) return false;

    const taskDate = new Date(`${normalizedDate}T12:00:00`);
    return taskDate.getFullYear() === year && taskDate.getMonth() === monthIndex;
  });

  const monthTitle = formatMonthTitle(year, monthIndex);
  const html = buildPrintDocumentHtml({
    scopeTitle: `Planning du mois - ${monthTitle}`,
    scopeSubtitle: 'Grille mensuelle imprimable avec cases du planning',
    tasks: periodTasks,
    forceSingleMonthPage: true,
    layoutMode: 'month-grid',
  });

  await exportPdfDirectIfAvailable({
    fileName: `planning-mois-${year}-${String(monthIndex + 1).padStart(2, '0')}.pdf`,
    html,
  });
};

const handleSelectTheme = async (themeId) => {
  if (!themeMenuTask) {
    return;
  }

  const taskId = themeMenuTask.id;
  const normalizedThemeId =
    themeId === null ? null : Number(themeId);

  try {
    setThemeError('');

    const response = await fetch('/go/php/tasks/update-task-theme.php', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        task_id: taskId,
        theme_id: normalizedThemeId,
      }),
    });

    const data = await response.json();

    if (!response.ok || data?.success === false) {
      throw new Error(
        data?.error || "Impossible d'enregistrer le thème."
      );
    }

    const selectedTheme =
      normalizedThemeId === null
        ? null
        : themes.find(
            (theme) => Number(theme.id) === normalizedThemeId
          );

    setTasks((currentTasks) =>
      currentTasks.map((task) =>
        Number(task.id) === Number(taskId)
          ? {
              ...task,
              theme: normalizedThemeId,
              theme_name: selectedTheme?.nom ?? null,
              theme_icon: selectedTheme?.icon ?? null,
              theme_color: selectedTheme?.couleur ?? null,
            }
          : task
      )
    );

    setThemeMenuTask(null);
  } catch (error) {
    console.error('Erreur changement de thème :', error);
    setThemeError(error.message);
  }
};

  const handlePrintCurrentYear = async () => {
    const year = currentDate.getFullYear();
    const periodTasks = tasks.filter((task) => {
      const normalizedDate = normalizeDate(task.task_date);
      if (!normalizedDate) return false;

      const taskDate = new Date(`${normalizedDate}T12:00:00`);
      return taskDate.getFullYear() === year;
    });

    const html = buildPrintDocumentHtml({
      scopeTitle: `Planning annuel ${year}`,
      scopeSubtitle: '1 page par mois - version compacte type agenda',
      tasks: periodTasks,
      forceSingleMonthPage: true,
    });

    await exportPdfDirectIfAvailable({
      fileName: `planning-annuel-${year}.pdf`,
      html,
    });
  };

  const normalizeDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const formatSportMinutes = (totalMinutes) => {
    const minutes = Number(totalMinutes || 0);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    if (h > 0 && m > 0) return `${h} h ${String(m).padStart(2, '0')}`;
    if (h > 0) return `${h} h`;
    return `${m} min`;
  };

  const loadSportEntries = async () => {
    try {
      const response = await fetch('https://laforgeataches.fr/go/php/sport/get-sport-entries.php', {
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSportEntries(data.entries || []);
      } else {
        console.error('sport entries error:', data.message || data);
        setSportEntries([]);
      }
    } catch (error) {
      console.error('sport entries fetch error:', error);
      setSportEntries([]);
    }
  };

  const loadTasks = async () => {
    const data = await window.api.getTasks();
    setTasks(data || []);
  };

  const handleLoginSuccess = async (user) => {
    setAuthUser(user);
    await Promise.all([loadTasks(), loadSportEntries()]);
  };

  const handleLogout = async () => {
    try {
      await fetch('https://laforgeataches.fr/go/php/auth/logout.php', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('logout error:', error);
    } finally {
      setAuthUser(null);
      setTasks([]);
      setSportEntries([]);
      setSelectedTaskIds([]);
      setActiveView('tasks');
    }
  };



  useEffect(() => {
    async function checkSession() {
      try {
        const response = await fetch('https://laforgeataches.fr/go/php/auth/session-check.php', {
          method: 'GET',
          credentials: 'include',
        });

        const data = await response.json();

        if (response.ok && data.user) {
          setAuthUser(data.user);
          const tasksData = await window.api.getTasks();
          setTasks(tasksData || []);
          await loadSportEntries();
        } else {
          setAuthUser(null);
        }
      } catch (error) {
        console.error('session-check error:', error);
        setAuthUser(null);
      } finally {
        setAuthLoading(false);
      }
    }

    checkSession();
  }, []);


 const loadThemes = async () => {
  try {
    const response = await fetch('/go/php/tasks/get-themes.php', {
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || 'Impossible de charger les thèmes.');
    }

    const loadedThemes = Array.isArray(data) ? data : [];

    setThemes(loadedThemes);

    return loadedThemes;
  } catch (error) {
    console.error('Erreur chargement thèmes :', error);
    setThemeError(error.message);

    return [];
  }
};


useEffect(() => {
    if (authUser) {
    loadThemes();
  }
}, [authUser]);


  const handlePrintBlankMonth = async () => {
  const year = currentDate.getFullYear();
  const monthIndex = currentDate.getMonth();
  const monthTitle = formatMonthTitle(year, monthIndex);

  const html = buildPrintDocumentHtml({
    scopeTitle: `Planning vierge - ${monthTitle}`,
    scopeSubtitle: 'Grille mensuelle vide à imprimer',
    tasks: [],
    forceSingleMonthPage: true,
    layoutMode: 'month-grid',
    blankMonth: true,
    targetYear: year,
    targetMonthIndex: monthIndex,
  });

  await exportPdfDirectIfAvailable({
    fileName: `planning-vierge-${year}-${String(monthIndex + 1).padStart(2, '0')}.pdf`,
    html,
  });
};

  useEffect(() => {
    const handleResize = () => {
      const shouldCompact = window.innerWidth <= TASK_ACTIONS_COMPACT_BREAKPOINT;
      if (!shouldCompact) {
        setOpenMenuTaskId(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleDocumentClick = () => {
      setOpenMenuTaskId(null);
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  useEffect(() => {
    if (!undoAction) return undefined;

    const timer = setTimeout(() => {
      setUndoAction(null);
    }, 6000);

    return () => clearTimeout(timer);
  }, [undoAction]);


  const selectedTasks = useMemo(
    () => tasks.filter((task) => selectedTaskIds.includes(task.id)),
    [tasks, selectedTaskIds]
  );


const persistChangedTasks = async (previousTasks, updatedTasks) => {
  try {
    const changedTasks = updatedTasks.filter((updatedTask) => {
      const previousTask = previousTasks.find((task) => task.id === updatedTask.id);
      if (!previousTask) return false;

      return (
        normalizeDate(previousTask.task_date) !== normalizeDate(updatedTask.task_date) ||
        Number(previousTask.ordre_affichage || 0) !== Number(updatedTask.ordre_affichage || 0) ||
        (previousTask.title || '') !== (updatedTask.title || '') ||
        (previousTask.description || '') !== (updatedTask.description || '') ||
        (previousTask.status || 'todo') !== (updatedTask.status || 'todo') ||
        Number(previousTask.important || 0) !== Number(updatedTask.important || 0)
      );
    });

    await Promise.all(changedTasks.map((task) => window.api.updateTask(task)));
  } catch (error) {
    console.error('persistChangedTasks:', error);
  }
};

const getNextOrderForDate = (date, allTasks) => {
  const tasksForDate = allTasks.filter((t) => t.task_date === date);

  if (tasksForDate.length === 0) return 1;

  const maxOrder = Math.max(
    ...tasksForDate.map((t) => Number(t.ordre_affichage) || 0)
  );

  return maxOrder + 1;
};

const moveTaskToOrder = (allTasks, taskId, newOrder) => {
  const taskToMove = allTasks.find((t) => t.id === taskId);
  if (!taskToMove) return allTasks;

  const sameDayTasks = allTasks
    .filter((t) => t.task_date === taskToMove.task_date)
    .sort((a, b) => (a.ordre_affichage ?? 9999) - (b.ordre_affichage ?? 9999));

  const otherTasks = sameDayTasks.filter((t) => t.id !== taskId);

  let targetIndex = newOrder - 1;

  if (targetIndex < 0) targetIndex = 0;
  if (targetIndex > otherTasks.length) targetIndex = otherTasks.length;

  otherTasks.splice(targetIndex, 0, taskToMove);

  const reorderedSameDayTasks = otherTasks.map((task, index) => ({
    ...task,
    ordre_affichage: index + 1,
  }));

  return allTasks.map((task) => {
    if (task.task_date !== taskToMove.task_date) return task;

    const updated = reorderedSameDayTasks.find((t) => t.id === task.id);
    return updated || task;
  });
};

const renumberTasksForDate = (allTasks, date) => {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return allTasks;

  const tasksForDate = allTasks
    .filter((t) => normalizeDate(t.task_date) === normalizedDate)
    .sort((a, b) => (a.ordre_affichage ?? 9999) - (b.ordre_affichage ?? 9999));

  const reorderedMap = new Map(
    tasksForDate.map((task, index) => [
      task.id,
      {
        ...task,
        task_date: normalizedDate,
        ordre_affichage: index + 1,
      },
    ])
  );

  return allTasks.map((task) => reorderedMap.get(task.id) || task);
};

const moveTaskToDateAndRenumber = (allTasks, taskToMove, newDate) => {
  const oldDate = normalizeDate(taskToMove.task_date);
  const targetDate = normalizeDate(newDate);

  if (!oldDate || !targetDate) return allTasks;

  const destinationCount = allTasks.filter(
    (t) => t.id !== taskToMove.id && normalizeDate(t.task_date) === targetDate
  ).length;

  let updatedTasks = allTasks.map((task) =>
    task.id === taskToMove.id
      ? {
          ...task,
          task_date: targetDate,
          ordre_affichage: destinationCount + 1,
        }
      : task
  );

  updatedTasks = renumberTasksForDate(updatedTasks, oldDate);
  updatedTasks = renumberTasksForDate(updatedTasks, targetDate);

  return updatedTasks;
};


const handleOrderKeyDown = async (e, taskId) => {
  e.stopPropagation();

  if (e.key === 'Escape') {
    e.target.blur();
    return;
  }

  if (e.key !== 'Enter') return;

  const ordre = parseInt(e.target.value, 10);

  if (isNaN(ordre) || ordre < 1) {
    e.target.blur();
    return;
  }

  const updatedTasks = moveTaskToOrder(tasks, taskId, ordre);

  setTasks(updatedTasks);
  await persistChangedTasks(tasks, updatedTasks);
  await loadTasks();
  e.target.blur();
};

const handleOrderChange = async (taskId, newOrder) => {
  const ordre = parseInt(newOrder, 10);

  if (isNaN(ordre) || ordre < 1) return;

  const updatedTasks = moveTaskToOrder(tasks, taskId, ordre);

  setTasks(updatedTasks);
  await persistChangedTasks(tasks, updatedTasks);
  await loadTasks();
};

  const resetForm = () => {
    setEditingTaskId(null);
    setTitle('');
    setDescription('');
    setTaskDate('');
    setSelectedDate(null);
  };

  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
  };

  const insertBulletInDescription = () => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const value = description || '';
    const before = value.slice(0, start);
    const after = value.slice(end);
    const bullet = (before.length > 0 && !before.endsWith('\n') ? '\n' : '') + '• ';
    const nextValue = before + bullet + after;

    setDescription(nextValue);

    requestAnimationFrame(() => {
      const nextPos = start + bullet.length;
      textarea.focus();
      textarea.setSelectionRange(nextPos, nextPos);
    });
  };

  const insertCheckboxInDescription = () => {
    const textarea = descriptionTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const value = description || '';
    const before = value.slice(0, start);
    const after = value.slice(end);
    const checkbox = (before.length > 0 && !before.endsWith('\n') ? '\n' : '') + '- [ ] ';
    const nextValue = before + checkbox + after;

    setDescription(nextValue);

    requestAnimationFrame(() => {
      const nextPos = start + checkbox.length;
      textarea.focus();
      textarea.setSelectionRange(nextPos, nextPos);
    });
  };

  const insertTitleInDescription = () => {
    const trimmedTitle = String(title || '').trim();
    if (!trimmedTitle) return;

    const textarea = descriptionTextareaRef.current;
    if (!textarea) {
      setDescription((prev) => {
        const current = prev || '';
        return current.trim() ? `${current}\n${trimmedTitle}` : trimmedTitle;
      });
      return;
    }

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const value = description || '';
    const before = value.slice(0, start);
    const after = value.slice(end);
    const prefix = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
    const titleBlock = prefix + trimmedTitle;
    const nextValue = before + titleBlock + after;

    setDescription(nextValue);

    requestAnimationFrame(() => {
      const nextPos = start + titleBlock.length;
      textarea.focus();
      textarea.setSelectionRange(nextPos, nextPos);
    });
  };

  const toggleDescriptionCheckboxAtIndex = async (task, lineIndex) => {
    const lines = String(task.description || '').split(/\r?\n/);
    if (lineIndex < 0 || lineIndex >= lines.length) return;

    const line = lines[lineIndex] || '';
    const match = line.match(CHECKBOX_LINE_REGEX);
    if (!match) return;

    const nextMark = /x/i.test(match[2] || '') ? ' ' : 'x';
    lines[lineIndex] = `${match[1] || ''}- [${nextMark}] ${match[3] || ''}`;

    await window.api.updateTask({
      ...task,
      description: lines.join('\n'),
    });

    await loadTasks();
  };


  const openBulkModal = () => {
    if (selectedTaskIds.length === 0) return;
    setBulkDate(selectedTasks[0]?.task_date || todayIso);
    setIsBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    setIsBulkModalOpen(false);
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const clearSelection = () => {
    setSelectedTaskIds([]);
    setShowOtherDatePicker(false);
  };

  const toggleTaskMenu = (taskId) => {
    setOpenMenuTaskId((prev) => (prev === taskId ? null : taskId));
  };

  const handleDayClick = (date) => {
    if (!date) return;

    setSelectedDate(date);
    setEditingTaskId(null);
    setTitle('');
    setDescription('');
    setTaskDate(date);
    setIsTaskModalOpen(true);

    setTimeout(() => {
      titleInputRef.current?.focus();
    }, 0);
  };

  const handleEdit = (task) => {
    setEditingTaskId(task.id);
    setTitle(task.title || '');
    setDescription(task.description || '');
    setTaskDate(task.task_date || '');
    setSelectedDate(task.task_date || null);
    setIsTaskModalOpen(true);

    setTimeout(() => {
      titleInputRef.current?.focus();
    }, 0);
  };

  const handleDelete = async (id) => {
    const taskToDelete = tasks.find((t) => t.id === id);
    const index = tasks.findIndex((t) => t.id === id);

    if (!taskToDelete || index === -1) return;

    setUndoAction({
      type: 'delete',
      task: taskToDelete,
      index,
    });

    await window.api.deleteTask(id);
    setSelectedTaskIds((prev) => prev.filter((taskId) => taskId !== id));
    await loadTasks();
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.length === 0) return;

    const deletedTasks = tasks
      .map((task, index) => ({ task, index }))
      .filter(({ task }) => selectedTaskIds.includes(task.id));

    if (deletedTasks.length === 0) return;

    setUndoAction({
      type: 'bulk-delete',
      tasks: deletedTasks,
    });

    await Promise.all(deletedTasks.map(({ task }) => window.api.deleteTask(task.id)));
    setSelectedTaskIds([]);
    setShowOtherDatePicker(false);
    setOpenMenuTaskId(null);
    await loadTasks();
  };

  const handleUndo = async () => {
    if (!undoAction) return;

    if (undoAction.type === 'delete' && undoAction.task) {
      const { id, ...taskWithoutId } = undoAction.task;
      await window.api.addTask({
        ...taskWithoutId,
      });
    }

    if (undoAction.type === 'bulk-delete' && Array.isArray(undoAction.tasks)) {
      for (const entry of undoAction.tasks) {
        const { id, ...taskWithoutId } = entry.task;
        await window.api.addTask({
          ...taskWithoutId,
        });
      }
    }

    setUndoAction(null);
    await loadTasks();
  };

  const handlePrint = (task) => {
    const printWindow = window.open('', '', 'width=700,height=500');

    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Impression tâche</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              padding: 24px;
              color: #111;
            }
            h1 {
              margin: 0 0 12px;
              font-size: 22px;
            }
            .meta {
              margin-bottom: 18px;
              font-size: 14px;
              color: #444;
            }
            .desc {
              font-size: 16px;
              line-height: 1.5;
            }
            .print-checkbox-line {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              margin: 6px 0;
            }
            .print-checkbox-box {
              width: 18px;
              height: 18px;
              border: 2px solid #111;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              font-size: 13px;
              font-weight: 700;
              flex-shrink: 0;
              margin-top: 2px;
            }
            .print-checkbox-line.is-checked span:last-child {
              text-decoration: line-through;
              opacity: 0.75;
            }
            .print-text-line-empty {
              height: 10px;
            }
          </style>
        </head>
        <body>
          <h1>${task.title || ''}</h1>
          <div class="meta">Date : ${task.task_date || ''}</div>
          <div class="desc">${renderDescriptionHtml(task.description || '').replace(/planner-/g, 'print-')}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    if (editingTaskId !== null) {
      const existingTask = tasks.find((task) => task.id === editingTaskId);

      await window.api.updateTask({
        ...(existingTask || {}),
        id: editingTaskId,
        title,
        description: description.trimEnd(),
        task_date: taskDate,
      });
    } else {
        await window.api.addTask({
          title,
          description: description.trimEnd(),
          task_date: taskDate,
          status: 'todo',
          ordre_affichage: getNextOrderForDate(taskDate, tasks),
          important: 0,
        });
      }

    resetForm();
    closeTaskModal();
    await loadTasks();
  };


  const handleSetPriority = async (task, priority) => {
    const currentPriority = Math.max(0, Math.min(4, Number(task.important) || 0));
    const nextPriority = currentPriority === priority ? 0 : priority;

    await window.api.updateTask({
      ...task,
      important: nextPriority,
    });

    await loadTasks();
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'done' ? 'todo' : 'done';

    await window.api.updateTask({
      ...task,
      status: newStatus,
    });

    await loadTasks();
  };

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropTask = async (dateStr) => {
    if (!draggedTask) return;

    const sourceDate = normalizeDate(draggedTask.task_date);
    const targetDate = normalizeDate(dateStr);

    if (!targetDate || sourceDate === targetDate) {
      setDraggedTask(null);
      return;
    }

    const updatedTasks = moveTaskToDateAndRenumber(tasks, draggedTask, targetDate);
    const changedTasks = updatedTasks.filter((updatedTask) => {
      const originalTask = tasks.find((task) => task.id === updatedTask.id);
      if (!originalTask) return false;

      return (
        normalizeDate(originalTask.task_date) !== normalizeDate(updatedTask.task_date) ||
        Number(originalTask.ordre_affichage || 0) !== Number(updatedTask.ordre_affichage || 0)
      );
    });

    setTasks(updatedTasks);
    setDraggedTask(null);

    await Promise.all(changedTasks.map((task) => window.api.updateTask(task)));
    await loadTasks();
  };

  const applyActionToSelection = async (updater) => {
    if (selectedTaskIds.length === 0) return;

    const selectedIdSet = new Set(selectedTaskIds);
    const updates = tasks
      .filter((task) => selectedIdSet.has(task.id))
      .map((task) => window.api.updateTask(updater(task)));

    await Promise.all(updates);
    setSelectedTaskIds([]);
    setOpenMenuTaskId(null);
    setIsBulkModalOpen(false);
    await loadTasks();
  };

  const handleBulkSetStatus = async (status) => {
    await applyActionToSelection((task) => ({
      ...task,
      status,
    }));
  };

  const handleBulkMoveToDate = async (date) => {
    if (!date) return;

    await applyActionToSelection((task) => ({
      ...task,
      task_date: date,
    }));
    setShowOtherDatePicker(false);
  };

  const handleOtherDateChange = async (e) => {
    const value = e.target.value;
    if (!value) return;
    await handleBulkMoveToDate(value);
  };

  const handleBulkReschedule = async () => {
    if (!bulkDate || selectedTaskIds.length === 0) return;
    await handleBulkMoveToDate(bulkDate);
  };

  const handleExportSelection = () => {
    if (selectedTasks.length === 0) return;

    const payload = selectedTasks.map((task) => ({
      id: task.id,
      title: task.title,
      description: task.description || '',
      task_date: task.task_date || '',
      status: task.status || 'todo',
    }));

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json;charset=utf-8',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `laforge-selection-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyTextToClipboard = async (text) => {
    const value = String(text || '').trim();
    if (!value) return false;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        return true;
      }

      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (error) {
      console.error('Erreur copie presse-papiers :', error);
      return false;
    }
  };

  const handleCopyTaskTitle = async (task) => {
    const copied = await copyTextToClipboard(task?.title || '');
    if (copied) {
      setOpenMenuTaskId(null);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const days = [];
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1;

    for (let i = 0; i < offset; i += 1) {
      days.push(null);
    }

    for (let i = 1; i <= totalDays; i += 1) {
      const date = new Date(year, month, i);
      const yearStr = date.getFullYear();
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const iso = `${yearStr}-${monthStr}-${dayStr}`;

      days.push({ day: i, date: iso });
    }

    return days;
  };

  const days = useMemo(() => getDaysInMonth(), [currentDate]);

  const tasksByDate = useMemo(() => {
    const grouped = {};

    for (const task of tasks) {
      const normalized = normalizeDate(task.task_date);
      if (!normalized) continue;

      if (!grouped[normalized]) {
        grouped[normalized] = [];
      }

      grouped[normalized].push(task);
    }

    Object.keys(grouped).forEach((dateKey) => {
      const sortedDayTasks = [...grouped[dateKey]].sort((a, b) => {
        if (taskDisplayMode === 'theme') {
          const getThemeName = (task) => {
            const directName = String(task.theme_name || '').trim();
            if (directName) return directName;

            const taskThemeId = Number(task.theme_id ?? task.theme ?? 0);
            const matchingTheme = themes.find(
              (theme) => Number(theme.id) === taskThemeId
            );

            return String(matchingTheme?.nom || '').trim();
          };

          // Les tâches sans thème sont placées à la fin.
          const themeA = getThemeName(a);
          const themeB = getThemeName(b);

          if (themeA && !themeB) return -1;
          if (!themeA && themeB) return 1;

          const themeCompare = themeA.localeCompare(themeB, 'fr', {
            sensitivity: 'base',
            numeric: true,
          });

          if (themeCompare !== 0) return themeCompare;
        }

        const orderA = Number(a.ordre_affichage ?? a.ordre ?? 9999);
        const orderB = Number(b.ordre_affichage ?? b.ordre ?? 9999);

        if (orderA !== orderB) return orderA - orderB;

        return String(a.title || '').localeCompare(String(b.title || ''), 'fr', {
          sensitivity: 'base',
          numeric: true,
        });
      });

      // CalendarDay retrie les tâches selon ordre_affichage.
      // En mode thème, on lui fournit donc un ordre d'affichage temporaire,
      // uniquement pour le rendu. L'ordre enregistré en base reste inchangé.
      grouped[dateKey] =
        taskDisplayMode === 'theme'
          ? sortedDayTasks.map((task, index) => ({
              ...task,
              ordre_affichage_original: task.ordre_affichage,
              ordre_affichage: index + 1,
            }))
          : sortedDayTasks;
    });

    return grouped;
  }, [tasks, themes, taskDisplayMode]);

  const sportByDate = useMemo(() => {
    const grouped = {};

    for (const entry of sportEntries) {
      const normalized = normalizeDate(entry.sport_date);
      if (!normalized) continue;

      grouped[normalized] = (grouped[normalized] || 0) + Number(entry.duration_minutes || 0);
    }

    return grouped;
  }, [sportEntries]);

  useEffect(() => {
    if (selectedTaskIds.length === 0) {
      setShowOtherDatePicker(false);
    }
  }, [selectedTaskIds.length]);

  if (authLoading) {
    return (
      <div className="app">
        <div className="task-form" style={{ maxWidth: '520px', margin: '40px auto' }}>
          <h2>Chargement…</h2>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="app">
        <LoginForm onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  const handleGoToTaskFromSearch = (task) => {
  const normalizedDate = normalizeDate(task.task_date);
  if (!normalizedDate) return;

  const date = new Date(`${normalizedDate}T12:00:00`);

  setCurrentDate(date);
  setSelectedDate(normalizedDate);
  setActiveView("tasks");

  setTimeout(() => {
    const element = document.querySelector(`[data-task-id="${task.id}"]`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 150);
};

  return (
    <div className={`app ${selectedTaskIds.length > 0 ? 'has-floating-selection-toolbar' : ''} ${undoAction ? 'has-undo-toolbar' : ''}`}>
      <MainNavigation
        activeView={activeView}
        onViewChange={setActiveView}
        authUser={authUser}
        onLogout={handleLogout}
        taskDisplayMode={taskDisplayMode}
        tasks={tasks}
        onGoToTask={handleGoToTaskFromSearch}
        onTaskDisplayModeChange={(nextMode) => {
          setTaskDisplayMode(nextMode);

          try {
            localStorage.setItem('lfat-task-display-mode', nextMode);
          } catch {
            // Le changement reste actif pour la session si le stockage est indisponible.
          }
        }}
      />

      {activeView === "hours" && <AnnualWorkHoursCalculator />}

      {activeView === 'tasks' && (
        <>
      <SelectionToolbar
        selectedCount={selectedTaskIds.length}
        selectedDate={selectedTasks[0]?.task_date}
        todayIso={todayIso}
        tomorrowIso={tomorrowIso}
        showOtherDatePicker={showOtherDatePicker}
        onToggleOtherDate={() => setShowOtherDatePicker((prev) => !prev)}
        onOtherDateChange={handleOtherDateChange}
        onSetStatus={handleBulkSetStatus}
        onMoveToDate={handleBulkMoveToDate}
        onPrintMonth={handlePrintCurrentMonth}
        onPrintBlankMonth={handlePrintBlankMonth}
        onPrintYear={handlePrintCurrentYear}
        onExport={handleExportSelection}
        onDelete={handleBulkDelete}
        onClear={clearSelection}
      />

      <UndoToolbar action={undoAction} onUndo={handleUndo} />

      <CalendarNavigation
        currentDate={currentDate}
        onChange={setCurrentDate}
      />

      <div className="calendar">
        <div className="calendar-header">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
            <div key={d} className="calendar-cell header">
              {d}
            </div>
          ))}
        </div>

        <div className="calendar-grid">
          {days.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="calendar-cell empty" />;
            }

            return (
              <CalendarDay
                key={day.date}
                day={day}
                todayIso={todayIso}
                selectedDate={selectedDate}
                tasks={tasksByDate[day.date] || []}
                sportMinutes={sportByDate[day.date] || 0}
                formatSportMinutes={formatSportMinutes}
                selectedTaskIds={selectedTaskIds}
                openMenuTaskId={openMenuTaskId}
                parseDescriptionLines={parseDescriptionLines}
                onDayClick={handleDayClick}
                onDragOver={handleDragOver}
                onDropTask={handleDropTask}
                onOpenSport={() => setActiveView('sport')}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onToggleStatus={handleToggleStatus}
                onEdit={handleEdit}
                onToggleDescriptionCheckbox={toggleDescriptionCheckboxAtIndex}
                onSetPriority={handleSetPriority}
                onOrderKeyDown={handleOrderKeyDown}
                onToggleMenu={toggleTaskMenu}
                onToggleSelection={toggleTaskSelection}
                onCopyTitle={handleCopyTaskTitle}
                onPrint={handlePrint}
                onDelete={handleDelete}
                onCloseMenu={() => setOpenMenuTaskId(null)}
                onOpenThemeMenu={handleOpenThemeMenu}
              />
            );
          })}
        </div>
      </div>

      <TaskModal
        isOpen={isTaskModalOpen}
        editingTaskId={editingTaskId}
        title={title}
        description={description}
        taskDate={taskDate}
        titleInputRef={titleInputRef}
        descriptionTextareaRef={descriptionTextareaRef}
        onTitleChange={(event) => setTitle(event.target.value)}
        onDescriptionChange={(event) => setDescription(event.target.value)}
        onDateChange={(event) => setTaskDate(event.target.value)}
        onInsertBullet={insertBulletInDescription}
        onInsertCheckbox={insertCheckboxInDescription}
        onInsertTitle={insertTitleInDescription}
        onSubmit={handleSubmit}
        onClose={() => {
          resetForm();
          closeTaskModal();
        }}
      />

      <BulkActionsModal
        isOpen={isBulkModalOpen}
        selectedCount={selectedTaskIds.length}
        bulkDate={bulkDate}
        onBulkDateChange={(event) => setBulkDate(event.target.value)}
        onReschedule={handleBulkReschedule}
        onExport={handleExportSelection}
        onClose={closeBulkModal}
      />
        </>
      )}

      {activeView === 'lists' && <ListesPermanentes />}

      {activeView === 'ledger' && <MiniLedger />}

      {activeView === 'sport' && ( <SportTracker /> )}

      {themeMenuTask && (
      <div
        className="theme-modal-overlay"
        onClick={() => setThemeMenuTask(null)}
      >
        <div
          className="theme-modal-content"
          onClick={(event) => event.stopPropagation()}
        >
          <ThemeMenu
            themes={themes}
            currentTheme={themeMenuTask.theme ?? null}
            onSelect={handleSelectTheme}
            onClose={() => setThemeMenuTask(null)}
          />
        </div>
      </div>
    )}

    </div>
    
  );


  

}

export default App;
